import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Cerere, Uat } from '../../cereri.interface';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CereriService } from '../../cereri.service';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { initializeEditor } from 'src/app/utils/angular-editor/editorConfig';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { requireSingleInstitutionValidator } from 'src/app/validators/just-one-institution';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { map, startWith, finalize } from 'rxjs';
import { CommonService } from 'src/app/services/common.service';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { UatService } from 'src/app/platform/nomenclatoare/uat/uat.service';
import { helper } from 'src/environments/helper';
import { NotificationService } from 'src/app/services/notification.service';
import { AppFilesService } from 'src/app/services/app-files.service';

@Component({
    selector: 'app-document-uat',
    templateUrl: './document-uat.component.html',
    styleUrls: ['./document-uat.component.scss']
})
export class DocumentUatComponent implements OnInit {
    @Input() cerere!: Cerere;
    @Output() cerereChanged: EventEmitter<Cerere> = new EventEmitter<Cerere>();
    @Output() istoricUpdated: EventEmitter<void> = new EventEmitter();

    // general error
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;

    // general success alert
    successTitle: string = environment.config.customNotifications.headers.successForm;
    successIcon: string = environment.config.customNotifications.icons.success;
    successType: string = environment.config.customNotifications.icons.success;

    isLoading = false;
    isExportLoading = false;
    showForm = false;
    editorConfig!: AngularEditorConfig;

    form!: FormGroup;
    formUat!: FormGroup;

    separatorKeysCodes: number[] = [ENTER, COMMA];
    institutions: Uat[] = [];
    allInstitutions: Uat[] = [];
    filteredInstitutions!: any;
    institutionsCtrl = new FormControl('');

    uploading: boolean[] = [];

    @ViewChild('institutionInput') institutionInput!: ElementRef<HTMLInputElement>;

    constructor(
        private cereriService: CereriService,
        private fb: FormBuilder,
        private commonService: CommonService,
        public toastr: ToastrService,
        private uatService: UatService,
        private notificationService: NotificationService,
        private appFileService: AppFilesService,
    ) { }

    ngOnInit(): void {
        this.editorConfig = initializeEditor();

        this.getInstitution();

        this.form = this.fb.group({
            cerere_id: new FormControl(this.cerere.id_cerere),
            id_institutie: new FormControl(''),
            denumire_institutie: new FormControl(this.institutions, [Validators.required]),
            inst: new FormControl(''),
            email_institutie: new FormControl(''),
            notificare_uat: new FormControl(this.cerere.notificareUat || '', [Validators.required]),
            files: new FormControl(),
        })

        this.formUat = this.fb.group({
            denumire_institutie: new FormControl('', [Validators.required]),
            email_institutie: new FormControl('', [Validators.required]),
            locatie: new FormControl('', [Validators.required]),
            telefon: new FormControl('')
        })

        this.filteredInstitutions = this.institutionsCtrl.valueChanges.pipe(
            startWith(null),
            map((value: string | null) =>
                value ? this._institutionFilter(value) : this.allInstitutions.slice()
            )
        )
    }

    exportDocumentUat() {
        this.isExportLoading = true;
        let data = {
            cerere_id: this.cerere.id_cerere,
            notificare_uat: this.form.get('notificare_uat')?.value,
        }

        this.cereriService.exportDocumentUat(data)
            .subscribe({
                next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);

                    window.open(url);

                    this.isExportLoading = false;
                },
                error: (err: any) => {
                    console.log(err);
                    this.isExportLoading = false;
                },
            })
    }

    retrimiteNotificare() {
        this.isLoading = true;

        const updatedData = {
            id_info: this.cerere.info.id_info,
            id_cerere: this.cerere.id_cerere,
            id_cerere_history: this.cerere.notificare_uat_history.id_cerere_history,
        };

        this.form.get('notificare_uat')?.patchValue(this.cerere.notificareUat);

        this.cereriService
            .resetEmail(updatedData)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: () => {
                    this.getRequestInfo();
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                }
            })
    }

    showFormUat() {
        this.showForm = !this.showForm;
    }

    resetControlsState(formGroup: FormGroup) {
        formGroup.reset();
        formGroup.setErrors({ 'invalid': true });
        Object.keys(formGroup.controls).forEach(controlName => {
            const control = formGroup.get(controlName);
            control?.setErrors(null);
            control?.markAsPristine();
            control?.markAsUntouched();
        });
    }

    submitFormUat() {
        this.isLoading = true;

        const uat = {
            denumire_institutie: this.formUat.get('denumire_institutie')?.value,
            email_institutie: this.formUat.get('email_institutie')?.value,
            locatie: this.formUat.get('locatie')?.value,
            telefon: this.formUat.get('telefon')?.value,
        };

        this.uatService
            .addUat(uat)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.getRequestInfo();
                    this.resetControlsState(this.formUat);
                    this.showFormUat();
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    submitForm() {
        this.isLoading = true;

        const filesToSend = this.form.get('files')?.value || [];
        let formData = new FormData();

        Object.keys(this.form.value).forEach(key => {
            if (key !== 'files') {
                const value = this.form.value[key];
                if (value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            }
        })

        filesToSend.forEach((file: any, index: any) => {
            formData.append(`files-${index}`, file);
        });

        if (this.form.get('denumire_institutie'))

            this.cereriService
                .sendEmail(formData)
                .pipe(finalize(() => (this.isLoading = false)))
                .subscribe({
                    next: (res: any) => {
                        let response = (typeof res.status_code !== 'undefined' ? res : res.error);
                        if (typeof response.status_code !== 'undefined') {
                            if (response.status_code == 200 && typeof response.data !== 'undefined') {
                                this.isLoading = false;
                                this.notificationService.warningSwal(this.successTitle, 'Înștiințarea dvs. a fost trimisă cu succes', this.successIcon);
                                this.getRequestInfo();
                            }
                        } else {
                            let errorMessage = environment.config.customNotifications.generalMessages.error;
                            this.isLoading = false;
                            this.notificationService.warningSwal(this.errorTitle, errorMessage, this.errorIcon);
                        }
                    },
                    error: (res: any) => {
                        this.isLoading = false;
                        this.onHttpError(res.error);
                        return false;
                    }
                })
    }

    private _institutionFilter(value: string | Uat): Uat[] {
        let filterValue = '';
        if (typeof value === 'string') {
            filterValue = value.toLowerCase();
        } else {
            filterValue = value.denumire_institutie.toLowerCase();
        }

        return this.allInstitutions.filter((institution) =>
            institution.denumire_institutie.toLowerCase().includes(filterValue)
        );
    }

    addInstitution(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();

        if (value) {
            const foundOption = this.allInstitutions.find(
                (institution) => institution.denumire_institutie.toLowerCase() === value.toLowerCase()
            );

            if (foundOption) {
                this.institutions.push(foundOption);
                this.updateInstitutieFields();
                this.institutionsCtrl?.disable();
            }
        }

        event.chipInput!.clear();
        this.institutionsCtrl.setValue(null);

    }

    removeInstitution(institution: Uat): void {
        const index = this.institutions.indexOf(institution);

        if (index >= 0) {
            this.institutions.splice(index, 1);

            this.updateInstitutieFields();
            this.institutionsCtrl?.enable();
        }
    }

    selectedInstitutions(event: MatAutocompleteSelectedEvent): void {
        const selectedOption = this.allInstitutions.find(
            (institution) => institution.denumire_institutie === event.option.viewValue
        );

        if (selectedOption && !this.institutions.find((institution) => institution.id_uat === selectedOption.id_uat)) {
            this.institutions.push(selectedOption);
            this.institutionInput.nativeElement.value = '';
            this.institutionsCtrl.setValue(null);

            this.updateInstitutieFields();
            this.institutionsCtrl?.disable();
        }
    }

    getInstitution() {
        this.allInstitutions = this.cerere.uat_list;
        console.log(this.allInstitutions);
    }

    private updateInstitutieFields(): void {
        const id_institutie = this.institutions.map(institution => institution.id_uat);
        const denumire_institutie = this.institutions.map(institution => institution.denumire_institutie);
        const inst = this.institutions.map(institution => institution.inst);
        const email_institutie = this.institutions.map(institution => institution.email_institutie);

        this.form.controls['id_institutie'].setValue(id_institutie);
        this.form.controls['id_institutie'].updateValueAndValidity();

        this.form.controls['denumire_institutie'].setValue(denumire_institutie);
        this.form.controls['denumire_institutie'].updateValueAndValidity();

        this.form.controls['inst'].setValue(inst);
        this.form.controls['inst'].updateValueAndValidity();

        this.form.controls['email_institutie'].setValue(email_institutie);
        this.form.controls['email_institutie'].updateValueAndValidity();
    }

    addFile(event: any) {
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            const currentFiles = this.form.get('files')?.value || [];
            currentFiles.push(file);
            this.form.get('files')?.setValue(currentFiles);

            this.uploading.push(true);

            setTimeout(() => {
                this.uploading.fill(false);

                this.openToast({
                    title: this.successTitle,
                    message: 'Fișierul dvs. a fost adăugat cu succes.',
                    type: this.successIcon
                });
            }, 1000);
        }
    }

    removeFile(index: number) {
        const currentFiles = this.form.get('files')?.value || [];
        currentFiles.splice(index, 1);
        this.uploading.splice(index, 1);
        this.form.get('files')?.setValue(currentFiles);
    }

    openToast(object: any) {
        const { type, message, title } = object;

        let options = this.toastr.toastrConfig;
        options.progressBar = true;
        options.preventDuplicates = true;
        options.closeButton = true;

        const inserted = this.toastr.show(message, title, options, options.iconClasses[type]);
    }

    getRequestInfo() {
        this.isLoading = true;
        this.cereriService
            .getRequestById(this.cerere.id_cerere)
            .pipe(map((response: any) => response.data))
            .subscribe((requestData) => {
                this.isLoading = false;
                this.cerere = requestData;
                this.cerereChanged.emit(this.cerere);
                this.istoricUpdated.emit();
                this.getInstitution();
            });
    }

    downloadDocument(file: any) {
        let fullPath = this.cereriService.downloadDocumentUrl + file.id_document;
        this.appFileService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('File download error: ', err);
                this.isLoading = false;
            })
    }

    formatCreatedAt(created_at: string): string {
        return new Date(created_at).toLocaleString('ro-RO');
    }

    onHttpError(res: any, showErrorsInToast: boolean = false) {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                if (showErrorsInToast) {
                    this.openToast({ title: this.errorTitle, message: msg, type: this.errorType });
                }
                else {
                    this.notificationService.warningSwal(this.errorTitle, msg, this.errorIcon);
                }
            });
        }
        else {
            let errorMessage = environment.config.customNotifications.generalMessages.error;
            this.notificationService.warningSwal(this.errorTitle, errorMessage, this.errorIcon);
        }
    }
}
